import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StatusBar } from '@capacitor/status-bar';
import { IonContent, IonSegment, ModalController, Platform } from '@ionic/angular';
import { CompanyFacilityService } from '@shared/facility-services/company.facility.service';
import { NotificationService } from '@shared/services/core/notification.service';
import { BehaviorSubject } from 'rxjs';
import SignaturePad from 'signature_pad';
import { DEFAULT_LANGUAGE, END_REVIEW_LANGUAGE } from './signature-consent-model';
import { SignatureConsentService, UploadResponse } from './signature-consent.service';

@Component({
    selector: 'signature-consent',
    templateUrl: 'signature-consent.component.html',
    styles: [
        `
			.signature-pad {
				border: 1px dashed #000;
				display: block;
			}
		`,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignatureConsent {
    @HostListener('window:resize')
    private resizeCanvas() {
        const canvas = this.signaturePadElement.nativeElement;
        const container = canvas.parentElement;
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
        this.signaturePad.clear();
    }

    public companyFacilityService = inject(CompanyFacilityService);

    @ViewChild('segment') segment: IonSegment;
    @ViewChild('signaturePad') signaturePadElement: ElementRef;
    @ViewChild(IonContent) content: IonContent;
    @Input() uploadPath: string;
    @Input() isInitials: boolean = false;
    public static isModalOpen: boolean = false;
    public hasConsented$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public disableSaveSignature$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public consentForm: FormGroup;
    public defaultLanguage: string = DEFAULT_LANGUAGE;
    public endReviewLanguage: string = END_REVIEW_LANGUAGE;
    private signaturePad: SignaturePad;


    constructor(
        private readonly service: SignatureConsentService,
        private readonly modalController: ModalController,
        private readonly fb: FormBuilder,
        private readonly notificationService: NotificationService,
        private readonly platform: Platform,
    ) {
        this.setupForm();
    }

    public async ionViewWillEnter() {
        if (this.platform.is('hybrid')) await StatusBar.hide();
    }

    public prepareSignaturePad() {
        if (this.signaturePad) {
            this.signaturePad.clear();
            return;
        }
        this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement, {
            backgroundColor: 'rgba(255, 255, 255, 0)',
            penColor: 'black',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
            throttle: 16,
        });
        this.resizeCanvas();
    }

    private cleanSignaturePad() {
        if (this.signaturePad) {
            this.signaturePad.clear();
            this.signaturePad.off();
            this.signaturePad = null;
        }
    }

    public async ionViewWillLeave() {
        this.cleanSignaturePad();
        if (this.platform.is('hybrid')) await StatusBar.show();
    }

    private setupForm() {
        this.consentForm = this.fb.group({
            canReadAndUnderstandConsent: [false, Validators.requiredTrue],
            consentToElectronicSignature: [false, Validators.requiredTrue],
        });
    }

    private goToSignatureOrInitials() {
        this.segment.value = this.isInitials ? 'Initials' : 'Signature';
    }

    public async agreeToConsent() {
        this.hasConsented$.next(true);
        const scrollElement = await this.content.getScrollElement();
        if (scrollElement.scrollTop !== 0) await this.content.scrollToTop(500);
        this.goToSignatureOrInitials();
    }

    public clear() {
        this.signaturePad.clear();
    }

    public async save() {
        this.disableSaveSignature$.next(true);
        if (this.signaturePad.isEmpty()) {
            this.notificationService
                .presentAlert({
                    message: `${this.isInitials ? 'Initials' : 'Signature'} required!`,
                })
                .subscribe();
            this.disableSaveSignature$.next(false);
            return;
        }
        const signatureDataUrl = this.signaturePad.toDataURL();
        this.service.trimAndUpload(signatureDataUrl, this.uploadPath).subscribe({
            next: async (res: UploadResponse) => {
                this.disableSaveSignature$.next(false);
                await this.dismiss(res);
            },
            error: async error => {
                this.notificationService.presentAlert({ message: 'Error uploading signature, please try again.' }).subscribe();
                this.disableSaveSignature$.next(false);
                await this.dismiss();
            },
        });
    }

    public async dismiss(response?: UploadResponse) {
        await this.modalController.dismiss(response);
    }
}
